import { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChannel } from '../contexts/ChannelContext';
import { useMapBackground } from '../contexts/MapBackgroundContext';
import {
  Layers, Menu, X, FolderOpen, CreditCard, FileSearch, ScanSearch,
  UserCircle, Wand2, Settings, Sun, Moon, MapPin, LogOut, Coins, Users, Boxes,
  ChevronLeft, ChevronRight, ChevronDown, Crosshair, Map as MapIcon, EyeOff, DoorOpen,
} from 'lucide-react';
import AppSwitcher from './AppSwitcher';
import { NotificationBell } from './NotificationBell';
import { HudGrainOverlay } from './hud';
import { ProjectFoldersNav } from './projects/ProjectFoldersNav';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/700.css';
import '@fontsource/outfit/600.css';

const THEME_KEY = 'shoebox_theme';
const COLLAPSED_KEY = 'hud_sidebar_collapsed';
const GROUPS_KEY = 'hud_sidebar_groups';
const LIGHT_MIGRATION_KEY = 'shoebox_theme_light_v16';

/**
 * HUDLayout — Layout principal des pages secondaires.
 * Sidebar allégée, rétractable (mode icônes), groupes repliables avec persistance localStorage.
 */
const HUDLayout = () => {
  const { user, logout, updateTheme } = useAuth();
  const { channel, isEnabled } = useChannel();
  const { visible: mapVisible, toggle: toggleMapBg } = useMapBackground();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // V14.68.22 — Auto-collapse la sidebar quand on est dans une page projet
  // (route /projects/:id) pour maximiser l'espace de travail. L'utilisateur
  // peut toujours ré-ouvrir manuellement via le bouton toggle.
  const [autoCollapsedByRoute, setAutoCollapsedByRoute] = useState(false);
  useEffect(() => {
    const isProjectDetail = /^\/projects\/[^/]+/.test(location.pathname);
    if (isProjectDetail && !autoCollapsedByRoute) {
      setCollapsed(true);
      setAutoCollapsedByRoute(true);
    } else if (!isProjectDetail && autoCollapsedByRoute) {
      // Restore user preference when leaving project pages
      try {
        const stored = localStorage.getItem(COLLAPSED_KEY);
        setCollapsed(stored === '1');
      } catch {
        setCollapsed(false);
      }
      setAutoCollapsedByRoute(false);
    }
  }, [location.pathname, autoCollapsedByRoute]);
  const [theme, setTheme] = useState('smooth');
  const [openGroups, setOpenGroups] = useState({
    workspace: true,
    tools: false,       // collapsed by default — secondary tools
    admin: true,
  });

  // Restore persisted UI state
  useEffect(() => {
    try {
      // V16.0 — la coque passe en clair par défaut (migration unique des anciens choix « dark »)
      if (!localStorage.getItem(LIGHT_MIGRATION_KEY)) {
        localStorage.setItem(LIGHT_MIGRATION_KEY, '1');
        localStorage.setItem(THEME_KEY, 'smooth');
      }
      const t = localStorage.getItem(THEME_KEY);
      if (t === 'smooth' || t === 'dark') setTheme(t);
      const c = localStorage.getItem(COLLAPSED_KEY);
      if (c === '1') setCollapsed(true);
      const g = localStorage.getItem(GROUPS_KEY);
      if (g) setOpenGroups((prev) => ({ ...prev, ...JSON.parse(g) }));
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    return () => document.documentElement.removeAttribute('data-theme');
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'smooth' : 'dark';
    setTheme(next);
    try { localStorage.setItem(THEME_KEY, next); } catch {}
    if (updateTheme) updateTheme(next);
  };

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(GROUPS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Hub principal = entrée mise en avant, hors groupes
  // V14.68.15 — Hub principal pointe désormais vers /studio/dashboard
  // (nouveau standard HUDLayout). L'ancienne /dashboard (cockpit fullscreen)
  // reste accessible via le bouton "Mode Cockpit" dans la topbar.
  const hubItem = { to: '/studio/dashboard', icon: MapPin, label: 'Hub principal' };

  // V14.1 — Sidebar refondue (Sprint B) : tout le métier sous "Espace de
  // travail" pour matcher la mental map du user (Dashboard / Projets /
  // Fichiers / Bibliothèques / Profil). Les outils techniques (plan-prep,
  // extracteur) sont regroupés en sous-section "Outils" pour ne pas polluer
  // la nav principale. Admin reste isolé en footer.
  const groups = useMemo(() => ([
    {
      id: 'workspace',
      label: 'Espace de travail',
      items: [
        // V14.68.15 — "Dashboard" retiré : Hub principal (au-dessus) pointe
        // désormais sur /studio/dashboard (évite la redondance).
        { to: '/hub/addresses',    icon: MapPin,     label: 'Mes lieux' },
        { to: '/projects',         icon: FolderOpen, label: 'Projets',       match: ['/projects'] },
        { to: '/studio/files',     icon: FileSearch, label: 'Fichiers' },
        { to: '/studio/library',   icon: Boxes,      label: 'Bibliothèques', match: ['/studio/library', '/materialtheque'] },
        { to: '/profile',          icon: UserCircle, label: 'Profil' },
      ],
    },
    {
      id: 'tools',
      label: 'Outils',
      items: [
        // V14.68.17 — 'Suivi' (/tracking) archivé (aucun usage interne).
        { to: '/plan-prep', icon: Wand2,      label: 'Préparation plan', flag: 'nav_plan_prep' },
        { to: '/extractor', icon: ScanSearch, label: 'Extracteur IA', flag: 'nav_extractor' },
        { to: '/lab/floorplan-extractor', icon: Crosshair, label: 'Plan → Vecteurs (POC)', flag: 'nav_floorplan_poc' },
        { to: '/crm',       icon: Users,      label: 'Mini-CRM', flag: 'nav_crm' },
        { to: '/billing',   icon: CreditCard, label: 'Facturation' },
      ].filter(it => !it.flag || isEnabled(it.flag)),
    },
    ...(user?.is_admin ? [{
      id: 'admin',
      label: 'Administration',
      items: [
        { to: '/generation', icon: Wand2,     label: 'Génération' },
        { to: '/admin',      icon: Settings,  label: 'Console admin' },
        { to: '/admin/pictograms', icon: DoorOpen, label: 'Pictos menuiserie (Lab)', flag: 'lab_door_first' },
      ].filter(it => !it.flag || isEnabled(it.flag)),
    }] : []),
  ]), [user?.is_admin, isEnabled]);

  const isActive = (item) => {
    if (item.match) return item.match.some(m => location.pathname.startsWith(m));
    return location.pathname === item.to;
  };

  const closeMobile = () => setMobileOpen(false);

  // Width tokens
  const sidebarWidth = collapsed ? 'lg:w-[68px]' : 'lg:w-64';
  const showLabels = !collapsed;

  const testidFor = (to) => `hud-nav-${to.replace(/\//g, '-').replace(/^-/, '')}`;

  // ---------- Item renderers ----------
  const renderHub = () => {
    const active = isActive(hubItem);
    const Icon = hubItem.icon;
    return (
      <NavLink
        to={hubItem.to}
        onClick={closeMobile}
        data-testid={testidFor(hubItem.to)}
        title={collapsed ? hubItem.label : undefined}
        className={`group flex items-center gap-2.5 ${collapsed ? 'lg:justify-center lg:px-0' : 'px-2.5'} py-2 rounded-lg transition-colors ${
          active
            ? 'bg-[var(--c-gold-soft)] text-[var(--c-gold)]'
            : 'text-[var(--c-gold)] hover:bg-[var(--c-gold-soft)]'
        }`}
      >
        <div className="w-7 h-7 rounded-md bg-[var(--c-gold)] flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-[var(--c-on-gold)]" />
        </div>
        {showLabels && (
          <div className="flex-1 min-w-0 leading-tight">
            <div className="text-[13px] font-medium">{hubItem.label}</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">
              Cockpit
            </div>
          </div>
        )}
      </NavLink>
    );
  };

  const renderItem = (item) => {
    const active = isActive(item);
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={closeMobile}
        data-testid={testidFor(item.to)}
        title={collapsed ? item.label : undefined}
        className={`relative flex items-center gap-2.5 ${collapsed ? 'lg:justify-center lg:px-0' : 'px-2.5'} py-1.5 rounded-md text-[12.5px] transition-colors ${
          active
            ? 'bg-[var(--c-surface-3)] text-[var(--c-text)]'
            : 'text-[var(--c-text-2)] hover:bg-[var(--c-surface-2)] hover:text-[var(--c-text)]'
        }`}
      >
        {active && (
          <span
            className={`absolute ${collapsed ? 'lg:left-0 lg:top-1.5 lg:bottom-1.5 lg:w-[2px]' : 'left-0 top-1.5 bottom-1.5 w-[2px]'} rounded-full bg-[var(--c-gold)]`}
          />
        )}
        <Icon className="w-[15px] h-[15px] shrink-0" />
        {showLabels && <span className="flex-1 truncate">{item.label}</span>}
      </NavLink>
    );
  };

  const renderGroup = (group) => {
    const isOpen = !!openGroups[group.id];
    // En mode rétracté on affiche directement les items, sans header
    if (collapsed) {
      return (
        <div key={group.id} className="hidden lg:flex flex-col gap-0.5">
          {group.items.map(renderItem)}
          <div className="my-1 mx-2 h-px bg-[var(--c-border)]" />
        </div>
      );
    }
    return (
      <div key={group.id} className="space-y-0.5">
        <button
          type="button"
          onClick={() => toggleGroup(group.id)}
          data-testid={`hud-nav-group-${group.id}`}
          aria-expanded={isOpen}
          className="w-full flex items-center justify-between px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--c-text-faint)] hover:text-[var(--c-text-2)] hover:bg-[var(--c-surface-1)] transition-colors"
        >
          <span>{group.label}</span>
          <ChevronDown
            className={`w-3 h-3 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
          />
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className="space-y-0.5 pt-0.5">
            {group.items.map(renderItem)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="hud-app-root h-screen w-full flex flex-col overflow-hidden relative"
      data-theme={theme}
      data-testid="hud-layout"
      data-map-bg={mapVisible ? '1' : '0'}
      style={{
        fontFamily: 'Manrope, sans-serif',
        // Fond transparent quand le map background est actif — laisse
        // apparaître la carte satellite globale. Sinon fallback opaque.
        background: mapVisible ? 'transparent' : 'var(--c-bg)',
      }}
    >
      <HudGrainOverlay />
      {/* === Topbar === */}
      <div className="h-14 border-b border-[var(--c-border)] bg-[var(--c-shell)] flex items-center justify-between px-3 sm:px-6 shrink-0 z-50 smooth-topbar">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Desktop collapse toggle */}
          <button
            className="hidden lg:flex w-9 h-9 rounded-lg bg-[var(--c-surface-2)] border border-[var(--c-border)] items-center justify-center text-[var(--c-text-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-3)] transition-colors shrink-0"
            onClick={toggleCollapsed}
            data-testid="hud-collapse-toggle"
            aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
            title={collapsed ? 'Déplier le menu' : 'Replier le menu'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          {/* Mobile menu */}
          <button
            className="lg:hidden w-9 h-9 rounded-lg bg-[var(--c-surface-2)] border border-[var(--c-border)] flex items-center justify-center text-[var(--c-text-2)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-3)] transition-colors shrink-0"
            onClick={() => setMobileOpen(true)}
            data-testid="hud-mobile-menu-btn"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-lg bg-[var(--c-gold)] flex items-center justify-center shrink-0">
            <Layers className="w-4 h-4 text-[var(--c-on-gold)]" />
          </div>
          <div className="flex flex-col leading-none min-w-0">
            <span
              className="text-[var(--c-text)] font-semibold tracking-tight text-base sm:text-lg truncate"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Shoebox3D
            </span>
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.22em] font-bold text-[var(--c-text-faint)] mt-0.5">
              A <span className="text-[var(--c-gold)]">Peter's Universe</span> Module
            </span>
          </div>
          {channel !== 'stable' && (
            <span
              data-testid="channel-badge"
              title={channel === 'lab' ? 'Environnement expérimental (branche Lab)' : 'Prod interne — techniciens & partenaires'}
              className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-md text-[9px] uppercase tracking-widest font-bold border shrink-0 ${
                channel === 'lab'
                  ? 'bg-orange-500/15 text-orange-400 border-orange-500/40'
                  : 'bg-blue-500/15 text-blue-400 border-blue-500/40'
              }`}
            >
              {channel === 'lab' ? 'Lab' : 'Interne'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <AppSwitcher theme={theme} testid="hud-layout-app-switcher" />
          {/* V14.68.17 — Bouton "Mode Cockpit" retiré :
              /dashboard fusionné dans /studio/dashboard (Hub principal). */}
          <button
            onClick={toggleMapBg}
            data-testid="hud-layout-mapbg-toggle"
            className={`w-9 h-9 rounded-lg bg-[var(--c-surface-2)] border border-[var(--c-border)] flex items-center justify-center transition-colors ${
              mapVisible
                ? 'text-[var(--c-gold)] hover:bg-[var(--c-surface-3)]'
                : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-3)]'
            }`}
            title={mapVisible ? 'Masquer la carte de fond' : 'Afficher la carte de fond'}
            aria-label={mapVisible ? 'Masquer la carte de fond' : 'Afficher la carte de fond'}
            aria-pressed={mapVisible}
          >
            {mapVisible ? <MapIcon className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            onClick={toggleTheme}
            data-testid="hud-layout-theme-toggle"
            className="w-9 h-9 rounded-lg bg-[var(--c-surface-2)] border border-[var(--c-border)] flex items-center justify-center text-[var(--c-text-2)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-3)] transition-colors"
            title={theme === 'dark' ? 'Mode Smooth' : 'Mode Dark'}
            aria-label="Changer de thème"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <NotificationBell />
        </div>
      </div>

      {/* === Main: sidebar + page content === */}
      <div className="flex-1 flex overflow-hidden relative">
        {mobileOpen && (
          <div
            className="lg:hidden absolute inset-0 bg-black/30 z-40 backdrop-blur-sm"
            onClick={closeMobile}
            data-testid="hud-layout-backdrop"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:relative inset-y-0 left-0 top-14 lg:top-0
            w-[min(82vw,272px)] ${sidebarWidth} shrink-0
            bg-[var(--c-shell)] border-r border-[var(--c-border)]
            flex flex-col z-50 lg:z-40
            transition-[transform,width] duration-300 ease-out
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          data-testid="hud-layout-sidebar"
          data-collapsed={collapsed ? '1' : '0'}
        >
          <button
            className="lg:hidden absolute top-2 right-2 w-8 h-8 rounded-md bg-[var(--c-surface-2)] border border-[var(--c-border)] flex items-center justify-center text-[var(--c-text-muted)] hover:text-[var(--c-text)] z-10"
            onClick={closeMobile}
            data-testid="hud-layout-sidebar-close"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>

          <nav className={`flex-1 ${collapsed ? 'lg:px-2' : 'px-3'} py-3 space-y-2 overflow-y-auto pt-4`}>
            {renderHub()}
            <div className={`${collapsed ? 'lg:my-2 lg:mx-2' : 'my-2'} h-px bg-[var(--c-border)]`} />
            {groups.map(renderGroup)}
            {location.pathname === '/projects' && (
              <>
                <div className={`${collapsed ? 'lg:my-2 lg:mx-2' : 'my-2'} h-px bg-[var(--c-border)]`} />
                <ProjectFoldersNav collapsed={collapsed} />
              </>
            )}
          </nav>

          {/* User footer */}
          <div className={`border-t border-[var(--c-border)] ${collapsed ? 'lg:p-2' : 'p-3'} shrink-0`}>
            <div className={`flex items-center gap-2.5 ${collapsed ? 'lg:justify-center lg:px-0 lg:py-1' : 'px-2 py-1.5'}`}>
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[var(--c-gold-soft)] flex items-center justify-center text-[var(--c-gold)] text-xs font-bold shrink-0">
                  {user?.name?.charAt(0) || '?'}
                </div>
              )}
              {showLabels && (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[var(--c-text)] truncate">{user?.name}</p>
                    <p className="text-[10px] text-[var(--c-text-faint)] flex items-center gap-1">
                      <Coins className="w-2.5 h-2.5" />
                      {user?.tokens || 0} jetons
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-[var(--c-text-faint)] hover:text-red-500 transition-colors"
                    title="Déconnexion"
                    data-testid="hud-layout-logout"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              {collapsed && (
                <button
                  onClick={handleLogout}
                  className="hidden lg:flex absolute right-2 bottom-2 w-7 h-7 rounded-md bg-[var(--c-surface-2)] border border-[var(--c-border)] items-center justify-center text-[var(--c-text-muted)] hover:text-red-500 transition-colors"
                  title="Déconnexion"
                  data-testid="hud-layout-logout-collapsed"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Page content */}
        <main className="flex-1 min-w-0 overflow-auto hud-page-container">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default HUDLayout;
